import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Paper,
} from '@material-ui/core';
import { RootStateOrAny, useSelector } from 'react-redux';
import styles from './styles';
import PopoverButton from '../PopoverButton/PopoverButton';
import StadiumLabel from '../StadiumLabel/StadiumLabel';
import headCells from '../../utils/constants/AttributeTableHeader/AttributeTableHeader';
import { Order } from '../../types/order';

import { format } from 'date-fns';
import { STATUS } from '../../types/status';

interface AttributesTableProps {
  onEditAttribute: (id: number) => void;
  onViewAttribute: (id: number) => void;
}

const AttributesTable: React.FC<AttributesTableProps> = ({
  onEditAttribute,
  onViewAttribute,
}) => {
  const classes = styles();

  const { attributeList: attributes } = useSelector(
    (state: RootStateOrAny) => state.AttributesReducer,
  );

  const { data: attributeList } = attributes;

  function compare(property, order: Order) {
    return (a, b) => {
      const propA = `${a[property] || ''}`.toUpperCase();
      const propB = `${b[property] || ''}`.toUpperCase();
      let result = 0;
      if (propA < propB) {
        result = -1;
      }
      if (propB < propA) {
        result = 1;
      }
      const orderMultiplier = order === 'desc' ? -1 : 1;
      return result * orderMultiplier;
    };
  }

  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<string>('update');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property);
  };

  attributeList?.sort(compare(orderBy, order));

  return (
    <Paper className={classes.root}>
      <TableContainer className={classes.container}>
        <Table
          stickyHeader
          aria-label="sticky table"
          size="small"
          padding="none"
        >
          <TableHead>
            <TableRow>
              {headCells.map(headCell => (
                <TableCell
                  className={classes.tableHeaderCell}
                  key={headCell.id}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={createSortHandler(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {attributeList?.map(val => {
              return (
                <TableRow key={val.id}>
                  <TableCell className={classes.tableCell}>
                    <Link onClick={() => onViewAttribute(val.id)} className={classes.link}>
                      {val.name}
                    </Link>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {val.region.map(val => val.region).join(',')}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {format(new Date(val.updatedDate), 'MM/dd/yyyy')}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {format(new Date(val.requestDate), 'MM/dd/yyyy')}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {val.userCreated}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <StadiumLabel text={STATUS[val.status]} />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <PopoverButton
                      onEdit={() => onEditAttribute(val.id)}
                      onView={() => onViewAttribute(val.id)}
                      attribute={val}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AttributesTable;
